import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Tab as TabType } from '../types';
import { IconRenderer } from './IconRenderer';

interface TabBarProps {
  tabs: TabType[];
  activeTabId: string;
  onTabChange: (tabId: string) => void;
  onTabCreate: () => void;
  onTabEdit: (tab: TabType) => void;
  onTabDelete: (tabId: string) => void;
  onTabReorder: (sourceIndex: number, destIndex: number) => void;
  editMode: boolean;
}

interface TabItemProps {
  tab: TabType;
  isActive: boolean;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
  editMode: boolean;
}

const TabItem: React.FC<TabItemProps> = ({
  tab,
  isActive,
  onClick,
  onEdit,
  onDelete,
  editMode
}) => {
  const theme = useTheme();

  const handleEdit = () => {
    onEdit();
  };

  const handleDelete = () => {
    onDelete();
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center'
      }}
    >
      <Tab
        value={tab.id}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconRenderer
              icon={tab.icon}
              iconType={tab.iconType}
              iconUrl={tab.iconUrl}
              size="small"
              color={isActive ? tab.color : theme.palette.text.primary}
            />
            <span>{tab.name}</span>
          </Box>
        }
        onClick={onClick}
        sx={{
          minHeight: 48,
          textTransform: 'none',
          fontWeight: isActive ? 700 : 600,
          color: isActive ? tab.color : '#333333',
          borderBottom: isActive ? `2px solid ${tab.color}` : 'none',
          '&:hover': {
            backgroundColor: alpha(tab.color, 0.08),
            color: tab.color
          },
          '& .MuiTab-wrapper': {
            color: 'inherit'
          },
          '& span': {
            fontWeight: 'inherit',
            color: 'inherit'
          }
        }}
      />
    </Box>
  );
};

const TabBar: React.FC<TabBarProps> = ({
  tabs,
  activeTabId,
  onTabChange,
  onTabCreate,
  onTabEdit,
  onTabDelete,
  onTabReorder,
  editMode
}) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedTab, setSelectedTab] = useState<TabType | null>(null);

  if (!tabs || tabs.length === 0) {
    return null;
  }

  // Ensure activeTabId exists in tabs, fallback to first tab
  const validActiveTabId = tabs.find(tab => tab.id === activeTabId)?.id || tabs[0]?.id || '';

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, tab: TabType) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedTab(tab);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedTab(null);
  };

  const handleEdit = () => {
    if (selectedTab) {
      onTabEdit(selectedTab);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedTab) {
      onTabDelete(selectedTab.id);
    }
    handleMenuClose();
  };


  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: theme.palette.background.paper,
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          minHeight: 48
        }}
      >
        <Box sx={{ position: 'relative', flex: 1 }}>
          <Tabs
            value={validActiveTabId}
            onChange={(_, newValue) => onTabChange(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': {
                display: 'none' // Custom indicator per tab
              }
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={tab.id}
                value={tab.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: editMode ? 3 : 0 }}>
                    <IconRenderer
                      icon={tab.icon}
                      iconType={tab.iconType}
                      iconUrl={tab.iconUrl}
                      size="small"
                      color={tab.id === validActiveTabId ? tab.color : theme.palette.text.primary}
                    />
                    <span>{tab.name}</span>
                    {editMode && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, tab);
                        }}
                        sx={{ 
                          position: 'absolute',
                          right: 4,
                          p: 0.5,
                          opacity: 0.7,
                          '&:hover': { 
                            opacity: 1,
                            backgroundColor: alpha(theme.palette.common.black, 0.04)
                          }
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                }
                sx={{
                  minHeight: 48,
                  textTransform: 'none',
                  color: tab.id === validActiveTabId ? tab.color : theme.palette.text.primary,
                  borderBottom: tab.id === validActiveTabId ? `2px solid ${tab.color}` : '2px solid transparent',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: alpha(tab.color, 0.08),
                  },
                  '&.Mui-selected': {
                    color: tab.color,
                  }
                }}
              />
            ))}
          </Tabs>
        </Box>
        
        {editMode && (
          <Tooltip title="Add Tab">
            <IconButton
              onClick={onTabCreate}
              sx={{
                ml: 1,
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main
                }
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit Tab
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete Tab
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TabBar;